import React from 'react';
import { observer } from 'mobx-react';
import { FormikHelpers, FormikProps } from 'formik';
import {
    AppPage,
    AppHeader,
    AppContent,
    AppSectionHeader,
    ActionList,
    Button,
    ButtonPrimary,
    Form,
    InputSwitch,
    AppFooter,
    useSnackbar
} from '../../components-v2/shared';
import { InputText, useAsyncEffect } from '@lambdacurry/component-library';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import {
    CSS,
} from '@dnd-kit/utilities';
import useStore from '../../store/useStore';
import { Company, DefaultMeetEliseModules, MeetEliseConfig, MeetEliseModule } from '../../types';
import { unsavedChangesContainerSelectorDefault } from '../../constants';
import { omit } from 'lodash';
import * as Yup from 'yup';

const companyOmittedValues = [
    'active_coupon_id',
    'custom_disclaimer',
    'deleted_at',
    'estimate_multiplier',
    'widget_logo',
    'rev_link_google_date',
    'rev_link_google_small',
    'rev_link_yelp_date',
    'rev_link_yelp_small',
    'full_name'
];

// Utility functions for module ordering
const ensureModulesHaveSortOrder = (modules: any) => {
    const moduleKeys = ['chat', 'tour', 'email', 'text', 'cost'];
    const result = { ...modules };

    moduleKeys.forEach((key, index) => {
        if (result[key] && typeof result[key].sortOrder === 'undefined') {
            result[key] = {
                ...result[key],
                sortOrder: (index + 1) * 10 // 10, 20, 30, 40, 50
            };
        }
    });

    return result;
};

const getSortedModuleEntries = (modules: any) => {
    const moduleEntries = Object.entries(modules || {});
    return moduleEntries.sort(([, a]: any, [, b]: any) => {
        const sortOrderA = a?.sortOrder || 0;
        const sortOrderB = b?.sortOrder || 0;
        return sortOrderA - sortOrderB;
    });
};

const updateModuleSortOrders = (modules: any, oldIndex: number, newIndex: number) => {
    const sortedEntries = getSortedModuleEntries(modules);
    const reorderedEntries = arrayMove(sortedEntries, oldIndex, newIndex);

    const updatedModules = { ...modules };
    reorderedEntries.forEach(([key, module]: any, index) => {
        updatedModules[key] = {
            ...module,
            sortOrder: (index + 1) * 10
        };
    });

    return updatedModules;
};

// Module titles for display
const MODULE_TITLES: Record<string, string> = {
    chat: 'Chat',
    tour: 'Tour',
    email: 'Email',
    text: 'Text',
    cost: 'Calculator'
};

interface MeetEliseConfigFormValues {
    meet_elise_config: MeetEliseConfig;
}

interface SortableModuleSectionProps {
    moduleKey: string;
    module: MeetEliseModule;
    formikProps: FormikProps<MeetEliseConfigFormValues>;
    company: Company;
}

const SortableModuleSection: React.FC<SortableModuleSectionProps> = ({
    moduleKey,
    module,
    formikProps,
    company
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: moduleKey });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition || undefined,
        opacity: isDragging ? 0.5 : 1,
    };

    const moduleTitle = MODULE_TITLES[moduleKey] || moduleKey;
    const defaultTitle = DefaultMeetEliseModules[moduleKey as keyof typeof DefaultMeetEliseModules]?.title || '';

    return (
        <div ref={setNodeRef} style={style}>
            <AppSectionHeader
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                            {...attributes}
                            {...listeners}
                            style={{
                                cursor: 'grab',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#666'
                            }}
                            title="Drag to reorder"
                        >
                            ⋮⋮
                        </div>
                        {moduleTitle}
                    </div>
                }
            />
            <div className="field-group-content">
                <InputSwitch
                    labelOn="Active"
                    labelOff="Inactive"
                    labelPlacement="start"
                    name={`meet_elise_config.modules.${moduleKey}.enabled`}
                    formikProps={formikProps}
                    checked={module?.enabled || false}
                />
                <InputText
                    name={`meet_elise_config.modules.${moduleKey}.title`}
                    label={`${moduleTitle} Button Title`}
                    labelPlacement="inset"
                    placeholder={company?.meet_elise_config?.modules?.[moduleKey as keyof typeof company.meet_elise_config.modules]?.title || defaultTitle}
                    disabled={!module?.enabled}
                    formikProps={formikProps}
                />
            </div>
        </div>
    );
};

export const MeetEliseWidget = observer(() => {
    const { addSnackbar } = useSnackbar();
    const { store } = useStore();
    const { router, fetchCompanies, companies } = store;
    const { companyId } = router.params;
    const company = companies.getItem(parseInt(companyId, 10));

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const fetchRequiredData = async () => {
        await fetchCompanies();
    };
    useAsyncEffect(fetchRequiredData);

    const validationSchema = Yup.object().shape({
        meet_elise_config: Yup.object().nullable()
    });

    const formatDataToSave = (values: MeetEliseConfigFormValues) => {
        const companyValues = omit(company, companyOmittedValues);
        const { meet_elise_config: meet_elise_config_from_form, ...formValues } = values;

        // Ensure modules have sortOrder values
        const modulesWithSortOrder = ensureModulesHaveSortOrder({
            ...DefaultMeetEliseModules,
            ...(meet_elise_config_from_form.modules || {})
        });

        return {
            ...companyValues,
            ...formValues,

            // Since we are using the `companies` API, we are essentially doing a patch here.
            // The API expects the `logo` field to be a base64 encoded data URL or `null`, so we are setting it to `null` to
            // avoid the "Input buffer contains unsupported image format" API error.
            // This is basically what is happening when you save the main company settings without adding/updating the logo image.
            logo: null,
            meet_elise_config: {
                ...meet_elise_config_from_form,
                enabled: company?.meet_elise_config?.enabled ?? false,
                modules: modulesWithSortOrder
            }
        };
    };

    const handleSubmit = async (
        values: MeetEliseConfigFormValues,
        actions: FormikHelpers<MeetEliseConfigFormValues>
    ) => {
        const data = formatDataToSave(values);

        try {
            await companies.update(data);
            addSnackbar('MeetElise widget config has been updated.', { variant: 'success' });
            actions.resetForm({ values: formatCompanyForForm(data as Company) });
        } catch (error) {
            addSnackbar('Failed to update MeetElise widget config.', { variant: 'error' });
            actions.setErrors(error.response.data);
        }
    };

    const handleToggleClick = async () => {
        if (!company) {
            return;
        }

        const data = omit(company, companyOmittedValues);

        const newConfig: MeetEliseConfig = {
            ...(data.meet_elise_config || false),
            modules: data.meet_elise_config?.modules || DefaultMeetEliseModules,
            enabled: !data.meet_elise_config?.enabled
        };

        try {
            await companies.update({
                ...data,
                meet_elise_config: newConfig
            });
            addSnackbar(
                `MeetElise widget integration has been ${data.meet_elise_config?.enabled ? 'deactivated' : 'activated'
                }.`,
                {
                    variant: 'success'
                }
            );
        } catch (error) {
            console.error(error.response.data);
            addSnackbar(
                `Failed to ${data.meet_elise_config?.enabled ? 'deactivate' : 'activate'
                } the MeetElise widget integration.`,
                {
                    variant: 'error'
                }
            );
        }
    };

    const formatCompanyForForm = (company: Company): MeetEliseConfigFormValues => {
        const modules = ensureModulesHaveSortOrder({
            ...DefaultMeetEliseModules,
            ...(company.meet_elise_config?.modules ?? {})
        });

        return {
            meet_elise_config: {
                enabled: company.meet_elise_config?.enabled ?? false,
                modules
            }
        };
    };

    const initialValues = (): Partial<Company> | any => (company ? formatCompanyForForm(company) : {});

    const handleDragEnd = (event: DragEndEvent, formikProps: FormikProps<MeetEliseConfigFormValues>) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const currentModules = formikProps.values.meet_elise_config?.modules || {};
        const sortedEntries = getSortedModuleEntries(currentModules);

        const oldIndex = sortedEntries.findIndex(([key]) => key === active.id);
        const newIndex = sortedEntries.findIndex(([key]) => key === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            const updatedModules = updateModuleSortOrders(currentModules, oldIndex, newIndex);
            formikProps.setFieldValue('meet_elise_config.modules', updatedModules);
        }
    };

    return (
        <AppPage loading={!company}>
            <AppHeader title="MeetElise Widget Configuration" icon={{ name: 'bellRing', color: 'blue' }}>
                <ActionList position="end">
                    <InputSwitch
                        labelOn="Active"
                        labelOff="Inactive"
                        labelPlacement="start"
                        onClick={handleToggleClick}
                        checked={company?.meet_elise_config?.enabled || false}
                    />
                </ActionList>
            </AppHeader>
            <div style={{ padding: '24px' }}>
                This tells the TCC application to move the MeetElise launcher buttons into itself. When this module is
                active, the original MeetElise launcher will be hidden. The corresponding modules in the TCC application
                will launch MeetElise instead.
            </div>

            {!!company && (
                <Form
                    validationSchema={validationSchema}
                    initialValues={initialValues()}
                    onSubmit={handleSubmit}
                    confirmUnsavedChanges
                    unsavedChangesConfig={{
                        containerQuerySelectorAll: unsavedChangesContainerSelectorDefault
                    }}
                >
                    {formikProps => {
                        const currentModules = formikProps.values.meet_elise_config?.modules || {};
                        const sortedModuleEntries = getSortedModuleEntries(currentModules);
                        const moduleIds = sortedModuleEntries.map(([key]) => key);

                        return (
                            <>
                                <AppContent className="field-group-content">
                                    <div className="field-group-col">
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={(event) => handleDragEnd(event, formikProps)}
                                        >
                                            <SortableContext items={moduleIds} strategy={verticalListSortingStrategy}>
                                                {sortedModuleEntries.map(([moduleKey, module]: any) => (
                                                    <SortableModuleSection
                                                        key={moduleKey}
                                                        moduleKey={moduleKey}
                                                        module={module}
                                                        formikProps={formikProps}
                                                        company={company!}
                                                    />
                                                ))}
                                            </SortableContext>
                                        </DndContext>
                                    </div>
                                </AppContent>

                                <AppFooter>
                                    <ActionList position="end">
                                        {formikProps.dirty && (
                                            <Button
                                                onClick={() => formikProps.resetForm()}
                                                disabled={formikProps.isSubmitting}
                                                data-lc-trigger-unsaved-changes={true}
                                                data-test-id="button_cancel"
                                            >
                                                Cancel
                                            </Button>
                                        )}
                                        <ButtonPrimary
                                            type="submit"
                                            disabled={!formikProps.dirty || formikProps.isSubmitting}
                                            data-test-id="button_save"
                                        >
                                            Save
                                        </ButtonPrimary>
                                    </ActionList>
                                </AppFooter>
                            </>
                        );
                    }}
                </Form>
            )}
        </AppPage>
    );
});
